import React, { useState, useEffect } from "react";
import {
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    FormFeedback,
    Row,
    Col,
} from "reactstrap";

const UserDetails = ({ user, apiBaseUrl, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        id: "",
        userName: "",
        email: "",
        firstName: "",
        lastName: "",
        officePhone: "",
        ext: "",
        homePhone: "",
        mobilePhone: "",
        active: true,
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);

    useEffect(() => {
        if (user) {
            setFormData({
                id: user.id || "",
                userName: user.userName || "",
                email: user.email || "",
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                officePhone: user.officePhone || "",
                ext: user.ext || "",
                homePhone: user.homePhone || "",
                mobilePhone: user.mobilePhone || "",
                active: user.active ?? true,
            });
        } else {
            setFormData({
                id: "",
                userName: "",
                email: "",
                firstName: "",
                lastName: "",
                officePhone: "",
                ext: "",
                homePhone: "",
                mobilePhone: "",
                active: true,
            });
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.userName) newErrors.userName = "Username is required";
        if (!formData.email) newErrors.email = "Email is required";
        if (!formData.firstName) newErrors.firstName = "First name is required";
        if (!formData.lastName) newErrors.lastName = "Last name is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError(null);

        if (!validate()) return;
        setLoading(true);

        try {
            const method = user ? "PUT" : "POST";
            const url = user ? `${apiBaseUrl}/${formData.id}` : apiBaseUrl;

            const response = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            if (!response.ok) throw new Error(`Server responded ${response.status}`);
            const result = await response.json();
            onSuccess(result);
        } catch (err) {
            setApiError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Username</Label>
                        <Input
                            name="userName"
                            value={formData.userName}
                            onChange={handleChange}
                            invalid={!!errors.userName}
                        />
                        <FormFeedback>{errors.userName}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>Email</Label>
                        <Input
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            invalid={!!errors.email}
                        />
                        <FormFeedback>{errors.email}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>First Name</Label>
                        <Input
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            invalid={!!errors.firstName}
                        />
                        <FormFeedback>{errors.firstName}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>Last Name</Label>
                        <Input
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            invalid={!!errors.lastName}
                        />
                        <FormFeedback>{errors.lastName}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label>Office Phone</Label>
                        <Input
                            name="officePhone"
                            value={formData.officePhone}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
                <Col md={2}>
                    <FormGroup>
                        <Label>Ext</Label>
                        <Input name="ext" value={formData.ext} onChange={handleChange} />
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <Label>Home Phone</Label>
                        <Input
                            name="homePhone"
                            value={formData.homePhone}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <Label>Mobile Phone</Label>
                        <Input
                            name="mobilePhone"
                            value={formData.mobilePhone}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
            </Row>

            <FormGroup check className="mb-3">
                <Label check>
                    <Input
                        type="checkbox"
                        name="active"
                        checked={formData.active}
                        onChange={handleChange}
                    />{" "}
                    Active
                </Label>
            </FormGroup>

            {apiError && <div className="text-danger mb-3">{apiError}</div>}

            <div className="d-flex justify-content-end gap-2">
                {onCancel && (
                    <Button color="secondary" onClick={onCancel} disabled={loading}>
                        Cancel
                    </Button>
                )}
                <Button color="primary" type="submit" disabled={loading}>
                    {loading ? "Saving..." : user ? "Update User" : "Create User"}
                </Button>
            </div>
        </Form>
    );
};

export default UserDetails;
